import { useSeoMeta } from '@unhead/react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useArchiveTagCounts } from '@/hooks/useArchiveTagCounts';
import { usePinnedNotes } from '@/hooks/usePinnedNotes';
import type { NostrEvent } from '@nostrify/nostrify';
import { ArchiveHeader } from '@/components/ArchiveHeader';
import { MasonryGrid } from '@/components/MasonryGrid';
import { AppliedFilters, ArchiveSearch, FilterSheet } from '@/components/ArchiveFilterBar';
import { CollectionBar } from '@/components/CollectionBar';
import { FormatSwitch } from '@/components/FormatSwitch';
import { ContactLauncher } from '@/components/ContactLauncher';
import { COLLECTIONS } from '@/lib/taxonomy';
import { profileUrlFor } from '@/lib/archiveProfile';
import { ARCHIVE_PUBKEY } from '@/hooks/useArchiveNotes';
import { nip19 } from 'nostr-tools';
import { BackToTop } from '@/components/BackToTop';
import { CollectionDeck } from '@/components/CollectionDeck';
import { CollectionInfo } from '@/components/CollectionInfo';
import { StartHere, pickStartRecord } from '@/components/StartHere';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Archive, Pin } from 'lucide-react';
import {
  applyFilters,
  collectionCounts,
  filterKey,
  hasFilters,
  indexNotes,
  parseFilters,
  termCounts,
  writeFilters,
  type ArchiveFilters,
} from '@/lib/archiveFilters';

/** The record formats offered as quick filters in the sticky strip. */
const FORMAT_CHIPS = [
  { slug: 'image', label: 'Image' },
  { slug: 'video', label: 'Video' },
  { slug: 'pdf', label: 'Doc' },
  { slug: 'text', label: 'Text' },
] as const;

/** Shared look for the footer's quiet text links. */
const footerLink =
  'font-engraved text-[10px] font-semibold uppercase tracking-[0.24em] text-foreground/70 underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

/** Offered when a search finds nothing; each is a subject the archive indexes. */
const SEARCH_SUGGESTIONS = [
  'Building 7',
  'Pentagon',
  'Flight 77',
  'Mossad',
  'Commission',
  'Bin Laden',
] as const;

// How many records to reveal per "page" of local pagination.
const DISPLAY_PAGE_SIZE = 60;

/** The remembered number of revealed records for a view, or the default. */
function readStoredCount(key: string): number {
  try {
    const n = Number(sessionStorage.getItem(key));
    return Number.isFinite(n) && n >= DISPLAY_PAGE_SIZE ? n : DISPLAY_PAGE_SIZE;
  } catch {
    return DISPLAY_PAGE_SIZE;
  }
}

const Index = () => {
  useSeoMeta({
    title: '9/11receipts — Archive & Directory',
    description:
      'A censorship-resistant archive of rare evidence, legal documents, FOIA disclosures, media records, and geopolitical history. Built as a resource for preservation, education, and litigation. Managed by 9/11 victim family members and survivors.',
    ogTitle: '9/11receipts — Archive & Directory',
    ogDescription:
      'A censorship-resistant archive of rare evidence, legal documents and geopolitical history.',
    twitterCard: 'summary',
  });

  // The COMPLETE archive is assembled here via a thorough time-windowed scan
  // across all relays (persisted + deduplicated). This is the single source of
  // truth for BOTH the feed and the tag counts, so the feed can never be
  // missing a record that the archive knows about.
  // `isLoading` is only true until the FIRST results arrive. The scan keeps
  // going after that (relays answer one by one and the counts keep rising), so
  // `isFetching` is what says "still working".
  const { data: tagData, isLoading, isFetching, isError } = useArchiveTagCounts();

  // Curated "must-have" notes, fetched by id across all relays so rare/old
  // records that relays don't serve in the feed still always appear.
  const { data: pinnedNotes } = usePinnedNotes();

   // Filters live in the URL (?t=…&c=…&q=…), so every view is linkable.
   const navigate = useNavigate();
   const location = useLocation();
   const [searchParams, setSearchParams] = useSearchParams();
   const filters = useMemo(() => parseFilters(searchParams), [searchParams]);
  const filtered = hasFilters(filters);
  const setFilters = (next: ArchiveFilters) => {
    // Typing in the search box replaces history entries rather than piling
    // them up, so Back still leaves the archive in one step.
    setSearchParams(writeFilters(searchParams, next), { replace: true });
  };

  // The ONE reset. "Explore all" (in the big block) and "All" (in the sticky
  // strip) both land here, and so does the empty-results "Clear all filters":
  // no category, no format, no subject filter, no search. That is also the state
  // the page loads in, so starting over and arriving are the same view.
  const resetToAll = () => setFilters({ terms: [], collection: null, query: '' });
  const isDefaultView = !filtered;

  // After a choice in the big block, carry the reader past it to the results.
  //
  // The scroll is only REQUESTED here; it happens in the layout effect below,
  // after React has rendered the new view. Scrolling straight away raced the
  // re-render: coming from a narrow filtered view the page was still short, so
  // the scroll stopped early or landed in the wrong place.
  const scrollPending = useRef(false);
  const [scrollTick, setScrollTick] = useState(0);
  const goToResults = () => {
    scrollPending.current = true;
    setScrollTick((t) => t + 1);
  };

   useLayoutEffect(() => {
     if (!scrollPending.current) return;
     scrollPending.current = false;
     // One more frame, so the masonry has laid out the new records first.
     const frame = requestAnimationFrame(() =>
       document.getElementById('archive-results')?.scrollIntoView({
         behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
           ? 'auto'
           : 'smooth',
         block: 'start',
       }),
     );
     return () => cancelAnimationFrame(frame);
   }, [scrollTick, searchParams]);

   // Scroll to archive feed when returning from Pager or Bitcoin pages
   useEffect(() => {
     const state = location.state as { scrollTo?: string } | null;
     if (state?.scrollTo === 'archive-feed') {
       // Use a small delay to ensure the DOM is ready
       const timer = setTimeout(() => {
         document.getElementById('archive-feed')?.scrollIntoView({
           behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
             ? 'auto'
             : 'smooth',
           block: 'start',
         });
       }, 50);
       return () => clearTimeout(timer);
     }
   }, [location]);

  // Ids that are pinned, so the chronological feed can skip them (they render
  // in their own always-visible section at the top instead of being buried by
  // date — important records are often old and would otherwise sort far down).
  const pinnedIds = useMemo(
    () => new Set((pinnedNotes ?? []).map(n => n.id)),
    [pinnedNotes],
  );

  // Pinned notes to display at the top (unfiltered view only), newest-first.
  const pinnedToShow = useMemo(
    () => [...(pinnedNotes ?? [])].sort((a, b) => b.created_at - a.created_at),
    [pinnedNotes],
  );

  // The full, sorted archive — pinned notes merged in, deduped, newest-first.
  const allNotes = useMemo(() => {
    const byId = new Map<string, NostrEvent>();
    for (const n of tagData?.notes ?? []) byId.set(n.id, n);
    for (const n of pinnedNotes ?? []) if (!byId.has(n.id)) byId.set(n.id, n);
    return Array.from(byId.values()).sort((a, b) => b.created_at - a.created_at);
  }, [tagData, pinnedNotes]);

  // Classify every record once; filtering and counting reuse this index.
  const indexed = useMemo(() => indexNotes(allNotes), [allNotes]);
  useEffect(() => {
    if (allNotes.length > 300 && !isFetching) void import('@/lib/tagAudit').then((m) => m.auditTags(allNotes));
  }, [allNotes, isFetching]);


  // The complete list for the current view: either everything (minus pinned,
  // which show above) or the filtered subset.
  const filteredNotes = useMemo(() => {
    if (filtered) return applyFilters(indexed, filters);
    return allNotes.filter(n => !pinnedIds.has(n.id));
  }, [indexed, allNotes, filters, filtered, pinnedIds]);

  // "Random record": a surprise from whatever is showing (the filtered view, or
  // the whole archive when nothing is filtered), opened like any record.
  const openRandom = () => {
    const pool = filtered ? filteredNotes : allNotes;
    if (pool.length === 0) return;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    navigate(`/${nip19.noteEncode(pick.id)}`, { state: { fromArchive: true } });
  };

  const activeCollection = COLLECTIONS.find((c) => c.id === filters.collection);

  // The longer "about this collection" explanation opens on request, from the
  // line above the records, instead of from a separate "i" on every card.
  const [aboutOpen, setAboutOpen] = useState(false);

  // One curated record to offer a first-time visitor as a place to begin.
  const startRecord = useMemo(() => pickStartRecord(pinnedToShow), [pinnedToShow]);

  // Format quick buttons (Image / Video / Document). They are ordinary filter
  // terms in the "format" facet, so they live in the URL and in the Filters
  // panel too, and the two always agree. As a one-tap switch they hold ONE
  // format at a time: picking another replaces it, and "Any" clears it. (If a
  // shared link or the Filters panel holds several, the first shows as active
  // and the next tap on the switch collapses them to one.)
  const activeFormat = useMemo(
    () => filters.terms.find((s) => FORMAT_CHIPS.some((f) => f.slug === s)) ?? null,
    [filters.terms],
  );
  const selectFormat = (slug: string | null) =>
    setFilters({
      ...filters,
      terms: [
        ...filters.terms.filter((s) => !FORMAT_CHIPS.some((f) => f.slug === s)),
        ...(slug ? [slug] : []),
      ],
    });

  // "Any" count: records matching the current category and search, whatever
  // their format. The per-format counts come from `facetCounts`, which already
  // ignores the format selection itself, so each number is "what you'd get".
  const formatAllCount = useMemo(
    () =>
      applyFilters(indexed, {
        ...filters,
        terms: filters.terms.filter((s) => !FORMAT_CHIPS.some((f) => f.slug === s)),
      }).length,
    [indexed, filters],
  );

  // Live facet counts (respecting the other active filters) and the exact
  // record count behind each Directory collection.
  const facetCounts = useMemo(() => termCounts(indexed, filters), [indexed, filters]);
  const directoryCounts = useMemo(
    () => (isLoading ? undefined : collectionCounts(indexed)),
    [indexed, isLoading],
  );

  // Local pagination: reveal DISPLAY_PAGE_SIZE at a time. Reset when the view
  // (tag) changes so each view starts from the top.
  //
  // The revealed count is remembered per view (sessionStorage) so that coming
  // Back from a record restores the same long page, letting the saved scroll
  // position land exactly where the reader left off.
  const countKey = `archive:visible:${filterKey(filters)}`;
  const [visibleCount, setVisibleCount] = useState(() => readStoredCount(countKey));

  // When the view (tag) changes, load that view's remembered count. Skipped on
  // first render, which already initialised from storage.
  const prevCountKey = useRef(countKey);
  useEffect(() => {
    if (prevCountKey.current === countKey) return;
    prevCountKey.current = countKey;
    setVisibleCount(readStoredCount(countKey));
  }, [countKey]);

  // Persist the revealed count for the current view.
  useEffect(() => {
    try {
      sessionStorage.setItem(countKey, String(visibleCount));
    } catch {
      /* non-fatal */
    }
  }, [countKey, visibleCount]);

  const notes = useMemo(
    () => filteredNotes.slice(0, visibleCount),
    [filteredNotes, visibleCount],
  );
  const hasMore = visibleCount < filteredNotes.length;

  // Progress: how many records are shown out of the full view total.
  const viewTotal = filteredNotes.length + (filtered ? 0 : pinnedToShow.length);
  const shownCount = notes.length + (filtered ? 0 : pinnedToShow.length);
  const showProgress = viewTotal > 0 && shownCount > 0;

  // Infinite scroll: reveal the next slice when the sentinel nears the viewport.
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const revealMore = () => setVisibleCount(c => c + DISPLAY_PAGE_SIZE);

  useEffect(() => {
    if (!hasMore) return;
    const el = loadMoreRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount(c => c + DISPLAY_PAGE_SIZE);
        }
      },
      { rootMargin: '800px 0px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, filteredNotes.length]);

  return (
    <div className="min-h-screen bg-background">
      {/* Skip link: invisible until a keyboard user tabs to it, then it jumps
          past the header bar and the category strip straight to the records. */}
      <a
        href="#archive-results"
        className="fixed left-3 top-3 z-[100] -translate-y-20 rounded-sm bg-foreground px-4 py-2 font-engraved text-[11px] font-semibold uppercase tracking-[0.2em] text-background transition-transform focus-visible:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
      >
        Skip to the records
      </a>

      <ArchiveHeader
        scanning={isFetching || isLoading}
        recordCount={allNotes.length}
        progress={showProgress ? { shown: shownCount, total: viewTotal } : undefined}
      />

      {/* ── The archive. Enter lands here. The Archive and the Directory are one
          thing: where to start (Explore all, the sections), then the toolbar
          (search, filters, record type) pinned beneath the header, then the
          records. ──────────────────────────────────────────────────────────── */}
      <div id="archive-feed" className="scroll-mt-12">
      <CollectionDeck
        total={isLoading ? undefined : allNotes.length}
        counts={directoryCounts}
        loading={isLoading}
        active={filters.collection}
        isDefault={isDefaultView}
        onRandom={openRandom}
        onSelect={(collection) => {
          // Choosing a category STARTS a fresh view of it: any leftover format,
          // subject filter or search from before is cleared, so what you see is
          // exactly what the card promised.
          setFilters({ terms: [], collection, query: '' });
          goToResults();
        }}
        onExploreAll={() => {
          resetToAll();
          goToResults();
        }}
      />
 
      {/* The one toolbar, pinned under the header for the whole feed: search,
          filters and the record type, once each. Once the section cards have
          scrolled away, a row of section chips hangs from it. */}
      <CollectionBar
        collections={COLLECTIONS}
        active={filters.collection}
        isDefault={isDefaultView}
        cardsId="archive-categories"
        counts={directoryCounts}
        total={isLoading ? undefined : allNotes.length}
        // In the chips, picking a section NARROWS what you're already looking
        // at (keeps your format and search) and "All" is the full reset.
        onSelect={(collection) => {
          if (collection === null) return resetToAll();
          // The special collections (Pager) have their own page, exactly as
          // their cards in the deck do, so a chip must not filter in place.
          const target = COLLECTIONS.find((c) => c.id === collection);
          if (target?.niche) return navigate(`/${target.id}`);
          setFilters({ ...filters, collection });
        }}
        search={
          <ArchiveSearch
            inline
            value={filters.query}
            onChange={(query) => setFilters({ ...filters, query })}
          />
        }
        filters={
          <FilterSheet
            inline
            filters={filters}
            onChange={setFilters}
            counts={facetCounts}
            resultCount={filteredNotes.length}
            loading={isLoading}
          />
        }
        formats={
          <FormatSwitch
            formats={FORMAT_CHIPS}
            activeFormat={activeFormat}
            formatCounts={facetCounts}
            formatAllCount={isLoading ? undefined : formatAllCount}
            onSelectFormat={selectFormat}
          />
        }
      />

      <main aria-label="Archive records" className="max-w-6xl mx-auto px-4 pb-8 pt-6">
        {/* Where the results begin (the category bar scrolls back to here, and
            the skip link lands here). tabIndex -1 lets it take focus so
            keyboard users continue reading from this point, not from the top. */}
        <div id="archive-results" tabIndex={-1} className="scroll-mt-52 outline-none" />

        {/* What the chosen category holds: the one-line explanation the
            Directory used to give, now shown where the reader is looking. */}
        {activeCollection && (
          <div className="mb-5">
            <p className="font-display text-base italic text-foreground/70">
              <span className="font-engraved text-[10px] font-semibold not-italic uppercase tracking-[0.24em] text-foreground">
                {activeCollection.title}
              </span>
              <span aria-hidden="true" className="mx-2 text-foreground/40">
                &mdash;
              </span>
              {activeCollection.caption}
            </p>
            {activeCollection.about && (
              <>
                <p className="mt-1.5 text-sm leading-snug text-foreground/80">
                  {activeCollection.about.split('. ')[0].replace(/\.$/, '')}.
                </p>
                <button
                  type="button"
                  onClick={() => setAboutOpen(true)}
                  aria-haspopup="dialog"
                  className="mt-1.5 inline-flex min-h-8 items-center font-engraved text-[10px] font-semibold uppercase tracking-[0.2em] text-foreground underline underline-offset-4 transition-colors hover:text-flag-red focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  About this collection
                </button>
              </>
            )}
          </div>
        )}

        {/* A place to begin: one curated record, only on the untouched view. */}
        {isDefaultView && startRecord && <StartHere note={startRecord.note} title={startRecord.title} />}

        {/* Only while a subject filter or search is active: what's applied, how
            many records match, and a way to clear it. */}
        <AppliedFilters
          filters={filters}
          onChange={setFilters}
          onStartOver={resetToAll}
          resultCount={filteredNotes.length}
          totalCount={allNotes.length}
        />

        {/* Loading state — masonry skeleton */}
        {isLoading && (
          <div className="flex gap-5 items-start">
            {[0, 1, 2].map(col => (
              <div key={col} className="flex-1 min-w-0 flex flex-col gap-5">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="rounded-2xl border border-foreground/20 overflow-hidden"
                  >
                    <Skeleton
                      className="w-full"
                      style={{ height: `${160 + (((col * 3 + i) * 53) % 160)}px` }}
                    />
                    <div className="p-4 space-y-2">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                      <div className="flex gap-1.5 pt-1">
                        <Skeleton className="h-5 w-14 rounded-full" />
                        <Skeleton className="h-5 w-10 rounded-full" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Error state */}
        {isError && !isLoading && (
          <div className="border border-dashed border-destructive rounded-3xl p-12 text-center">
            <Archive className="w-10 h-10 mx-auto mb-3 text-destructive/50" />
            <p className="font-typewriter text-sm uppercase tracking-wider text-muted-foreground">
              Unable to load archive. Check your relay connections and try again.
            </p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !isError && notes.length === 0 && (filtered || pinnedToShow.length === 0) && (
          <div className="border border-dashed border-foreground/30 rounded-3xl p-16 text-center">
            <Archive className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
            <p className="font-sans text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
              {filtered
                ? 'No records match these filters. Remove a filter above, or clear them all, to widen the search.'
                : 'No notes found in the archive yet. The relay may be syncing.'}
            </p>
            {filters.query.trim() && (
              <div className="mx-auto mt-6 max-w-md">
                <p className="font-engraved text-[10px] font-semibold uppercase tracking-[0.24em] text-foreground/70">
                  Try searching for
                </p>
                <ul className="mt-3 flex flex-wrap justify-center gap-2">
                  {SEARCH_SUGGESTIONS.map((term) => (
                    <li key={term}>
                      <button
                        type="button"
                        onClick={() => setFilters({ terms: [], collection: null, query: term })}
                        className="rounded-full border border-foreground/30 bg-card px-3 py-1.5 font-display text-[15px] leading-none text-foreground transition-colors hover:border-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                      >
                        {term}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {filtered && (
              <button
                type="button"
                onClick={resetToAll}
                className="mt-5 font-engraved text-[10px] font-semibold uppercase tracking-[0.2em] text-foreground underline underline-offset-4 hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}

        {/* Pinned records — always shown at the top of the unfiltered feed so
            important, often-old records are never buried by chronology. */}
        {!filtered && pinnedToShow.length > 0 && (
          <section className="mb-8">
            <div className="flex items-baseline justify-between gap-4 border-b border-foreground/25 pb-3 mb-4">
              <h2 className="flex items-center gap-2 font-engraved text-sm font-semibold uppercase tracking-[0.4em] text-foreground">
                <Pin className="h-3.5 w-3.5 text-flag-red" aria-hidden="true" />
                Pinned
              </h2>
              <p className="font-mono text-[11px] tabular-nums text-foreground/60">
                {pinnedToShow.length} records
              </p>
            </div>
            <MasonryGrid notes={pinnedToShow} />
          </section>
        )}

        {/* Masonry feed */}
        {notes.length > 0 && <MasonryGrid notes={notes} />}

        {/* Infinite scroll — a sentinel reveals the next slice of the complete
            archive as it nears the viewport; the button is an accessible
            fallback. Works for both the full feed and tag-filtered views. */}
        {!isLoading && hasMore && (
          <div ref={loadMoreRef} className="flex flex-col items-center gap-4 pt-8 pb-12">
            <Button
              variant="outline"
              size="lg"
              onClick={revealMore}
              className="gap-2 font-typewriter text-xs uppercase tracking-[0.2em] rounded-full border-foreground/40 px-8 hover:bg-foreground hover:text-background"
            >
              <Archive className="w-4 h-4" />
              Load more records
            </Button>
          </div>
        )}

        {/* End-of-archive marker so users know they've seen everything. */}
        {!isLoading && !hasMore && notes.length > 0 && (
          <div className="pt-8 pb-12 flex items-center justify-center gap-3">
            <span className="h-px w-12 bg-foreground/30" />
            <p className="text-[11px] text-muted-foreground font-typewriter uppercase tracking-[0.25em]">
              End{filtered ? '' : ' of archive'} · {viewTotal.toLocaleString()} records
            </p>
            <span className="h-px w-12 bg-foreground/30" />
          </div>
        )}

        {/* Footer */}
        <footer className="border-t-2 border-foreground/70 pt-8 pb-12 mt-8 text-center">
          {/* Official seal / wordmark */}
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-12 bg-foreground/30" />
            {/* The wordmark doubles as the way to get in touch: it opens the
                same About / contact panel as the Contact links. */}
            <ContactLauncher>
              {(openContact) => (
                <button
                  type="button"
                  onClick={openContact}
                  aria-label="9/11receipts — contact the archive"
                  className="flex items-center justify-center rounded-full border border-foreground/50 px-4 py-1.5 font-masthead text-sm leading-none text-foreground/80 transition-colors duration-200 [-webkit-tap-highlight-color:transparent] hover:border-foreground hover:bg-foreground hover:text-background active:border-foreground active:bg-foreground active:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  9/11receipts
                </button>
              )}
            </ContactLauncher>
            <div className="h-px w-12 bg-foreground/30" />
          </div>
          <p className="font-typewriter text-[10px] text-muted-foreground uppercase tracking-[0.3em] mb-3">
            Censorship-Resistant Archive
          </p>
          <p className="text-xs text-muted-foreground max-w-md mx-auto leading-relaxed">
            Managed by 9/11 victim family members and survivors. All records sourced directly
            from the Nostr network and cryptographically signed.
          </p>

          {/* Take part — the calls to action that used to live in the Directory. */}
          <nav
            aria-label="Take part"
            className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2"
          >
            <a
              href={profileUrlFor(nip19.npubEncode(ARCHIVE_PUBKEY))}
              target="_blank"
              rel="noopener noreferrer"
              className={footerLink}
            >
              Engage on Nostr
            </a>
            <button
              type="button"
              onClick={() => {
                setFilters({ terms: [], collection: 'resources', query: '' });
                // The reader is at the foot of the page; carry them up to see it.
                goToResults();
              }}
              className={footerLink}
            >
              Resources
            </button>
            <ContactLauncher>
              {(openContact) => (
                <button type="button" onClick={openContact} className={footerLink}>
                  Contact
                </button>
              )}
            </ContactLauncher>
          </nav>

          {/* Closes the armored block opened at the top of the page. */}
          <p
            aria-hidden="true"
            className="mt-6 select-none font-mono text-[10px] tracking-[0.2em] text-foreground/50"
          >
            -----END PUBLIC RECORD-----
          </p>
        </footer>
      </main>
      </div>

      <CollectionInfo
        collection={aboutOpen ? (activeCollection ?? null) : null}
        count={activeCollection ? directoryCounts?.[activeCollection.id] : undefined}
        onClose={() => setAboutOpen(false)}
        onSelect={() => setAboutOpen(false)}
      />

      <BackToTop />
    </div>
  );
};

export default Index;
